import { z } from "zod";

/**
 * The identity profile returned by login, refresh, invitation acceptance, and GET /auth/me/
 * (auth-v1.md "Identity profile response"). This is the ambient "who is the user" contract —
 * it lives in core so any module reads it from `@core` without a cross-module import.
 */
export const ProfileUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  full_name: z.string(),
  // The API contract still allows `ar`; the app ships fr and en only, so any other value reads
  // as French rather than failing the whole profile parse.
  preferred_language: z.enum(["fr", "en"]).catch("fr"),
  // Display label only ("president", "administrator", or null for accounts outside the new
  // model). Never branch on it: access follows `capabilities` and per-resource
  // `available_actions`, so a new role needs no frontend change.
  role: z.string().nullable().catch(null),
});

export const ProfileSchema = z.object({
  user: ProfileUserSchema,
  email_verified: z.boolean(),
  // e.g. "ACTIVE" | "PENDING_VERIFICATION"; kept open (string) — status set may grow and this
  // pass doesn't branch on it.
  account_status: z.string(),
  bundle_codes: z.array(z.string()),
  available_surfaces: z.array(z.string()),
  default_surface: z.string(),
  /**
   * What this account may do in the app at all (auth-v1.md "capabilities"): `team.read`, and
   * `team.invitation.manage` for the President. Cohort permissions are NOT here: they arrive per
   * resource as `available_actions`. Read it through `@core/permissions`, never directly.
   */
  capabilities: z.array(z.string()).default([]),
  session_id: z.string(),
  /** Safe post-auth redirect target; "" when none was supplied or stored (auth-v1.md). */
  next: z.string(),
});

export type ProfileUser = z.infer<typeof ProfileUserSchema>;
export type Profile = z.infer<typeof ProfileSchema>;
